import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../i18n';
import { supabase } from '../lib/supabase';
import AppHeader from '../components/AppHeader';
import { 
  Code, Copy, Eye, EyeOff, Key, 
  Globe, Shield, Zap, BookOpen, 
  Terminal, Download, ExternalLink,
  CheckCircle, AlertCircle, Clock
} from 'lucide-react';

interface ApiToken {
  id: string;
  token_name: string;
  token_value: string;
  permissions: string[];
  is_active: boolean;
  created_at: string;
  expires_at?: string;
}

const ApiDocumentation = () => {
  const { user } = useAuth();
  const { t, dir } = useLanguage();
  const [tokens, setTokens] = useState<ApiToken[]>([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [newTokenName, setNewTokenName] = useState('');
  const [showTokenValue, setShowTokenValue] = useState<string | null>(null);
  const [copiedToken, setCopiedToken] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'overview' | 'authentication' | 'endpoints' | 'examples' | 'tokens'>('overview');

  useEffect(() => {
    if (user) {
      fetchTokens();
    } else {
      setLoading(false);
    }
  }, [user]);

  // Reset activeTab if no user and it's 'tokens'
  useEffect(() => {
    if (!user && activeTab === 'tokens') {
      setActiveTab('overview');
    }
  }, [user, activeTab]);

  const fetchTokens = async () => {
    try {
      const { data, error } = await supabase
        .from('api_tokens')
        .select('*')
        .eq('user_id', user?.id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setTokens(data || []);
    } catch (err) {
      console.error('Error fetching tokens:', err);
    } finally {
      setLoading(false);
    }
  };

  const createToken = async () => {
    if (!newTokenName.trim()) return;

    setCreating(true);
    try {
      const tokenValue = `aud_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      
      const { data, error } = await supabase
        .from('api_tokens')
        .insert({
          user_id: user?.id,
          token_name: newTokenName,
          token_value: tokenValue,
          permissions: ['read', 'write'],
          is_active: true,
        })
        .select()
        .single();

      if (error) throw error;
      
      setTokens([data, ...tokens]);
      setNewTokenName('');
      setShowTokenValue(data.token_value);
    } catch (err) {
      console.error('Error creating token:', err);
    } finally {
      setCreating(false);
    }
  };

  const copyToClipboard = async (text: string, type: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedToken(type);
      setTimeout(() => setCopiedToken(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const toggleTokenVisibility = (tokenId: string) => {
    setShowTokenValue(showTokenValue === tokenId ? null : tokenId);
  };

  const codeExamples = {
    curl: `curl -X POST "https://api.render.audome.io/api/generate/PROJECT_ID" \\
  -H "Authorization: Bearer YOUR_API_TOKEN" \\
  -H "Content-Type: application/json" \\
  -d '{
    "data": {
      "title": "Hello World",
      "subtitle": "This is a subtitle"
    }
  }'`,

    javascript: `const response = await fetch('https://api.render.audome.io/api/generate/PROJECT_ID', {
  method: 'POST',
  headers: {
    'Authorization': 'Bearer YOUR_API_TOKEN',
    'Content-Type': 'application/json'
  },
  body: JSON.stringify({
    data: {
      title: 'Hello World',
      subtitle: 'This is a subtitle'
    }
  })
});

const result = await response.json();
console.log(result.image_url);`,

    python: `import requests

url = "https://api.render.audome.io/api/generate/PROJECT_ID"
headers = {
    "Authorization": "Bearer YOUR_API_TOKEN",
    "Content-Type": "application/json"
}
data = {
    "data": {
        "title": "Hello World",
        "subtitle": "This is a subtitle"
    }
}

response = requests.post(url, headers=headers, json=data)
result = response.json()
print(result['image_url'])`,

    php: `<?php
$url = "https://api.render.audome.io/api/generate/PROJECT_ID";
$headers = [
    "Authorization: Bearer YOUR_API_TOKEN",
    "Content-Type: application/json"
];
$data = [
    "data" => [
        "title" => "Hello World",
        "subtitle" => "This is a subtitle"
    ]
];

$ch = curl_init();
curl_setopt($ch, CURLOPT_URL, $url);
curl_setopt($ch, CURLOPT_POST, true);
curl_setopt($ch, CURLOPT_POSTFIELDS, json_encode($data));
curl_setopt($ch, CURLOPT_HTTPHEADER, $headers);
curl_setopt($ch, CURLOPT_RETURNTRANSFER, true);

$response = curl_exec($ch);
$result = json_decode($response, true);
echo $result['image_url'];
curl_close($ch);
?>`
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-white text-xl">{t('common.loading')}</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white" dir={dir} style={{ overflow: 'auto', height: '100vh' }}>
      <AppHeader />
      
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-900 to-blue-900 border-b border-gray-800">
        <div className="max-w-7xl mx-auto px-4 py-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-4xl font-bold mb-2">{t('pages.apiDocs.title')}</h1>
              <p className="text-gray-300 text-lg">{t('pages.apiDocs.subtitle')}</p>
            </div>
            <div className="flex items-center space-x-4 space-x-reverse">
              <div className="bg-green-600 px-4 py-2 rounded-lg flex items-center">
                <CheckCircle className="w-5 h-5 mr-2" />
                <span>{t('pages.apiDocs.apiActive')}</span>
              </div>
              <div className="bg-blue-600 px-4 py-2 rounded-lg flex items-center">
                <Clock className="w-5 h-5 mr-2" />
                <span>{t('pages.apiDocs.version')}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Sidebar */}
          <div className="lg:col-span-1">
            <div className="bg-gray-900 rounded-lg p-6 sticky top-8">
              <h3 className="text-xl font-bold mb-4 flex items-center">
                <BookOpen className="w-5 h-5 mr-2" />
                {t('pages.apiDocs.contents')}
              </h3>
              <nav className="space-y-2">
                {[
                  { id: 'overview', label: t('pages.apiDocs.overview'), icon: Globe },
                  { id: 'authentication', label: t('pages.apiDocs.authentication'), icon: Shield },
                  { id: 'endpoints', label: t('pages.apiDocs.endpoints'), icon: Terminal },
                  { id: 'examples', label: t('pages.apiDocs.codeExamples'), icon: Code },
                  ...(user ? [{ id: 'tokens', label: t('pages.apiDocs.tokenManagement'), icon: Key }] : []),
                ].map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id as any)}
                    className={`w-full text-right px-4 py-3 rounded-lg flex items-center transition-colors ${
                      activeTab === tab.id
                        ? 'bg-blue-600 text-white'
                        : 'text-gray-300 hover:bg-gray-800'
                    }`}
                  >
                    <tab.icon className="w-4 h-4 ml-3" />
                    {tab.label}
                  </button>
                ))}
              </nav>
            </div>
          </div>

          {/* Main Content */}
          <div className="lg:col-span-3">
            {activeTab === 'overview' && (
              <div className="space-y-8">
                <div className="bg-gray-900 rounded-lg p-8">
                  <h2 className="text-3xl font-bold mb-4 flex items-center">
                    <Globe className="w-8 h-8 mr-3 text-blue-400" />
                    {t('pages.apiDocs.overviewTitle')}
                  </h2>
                  <p className="text-gray-300 text-lg leading-relaxed mb-6">
                    {t('pages.apiDocs.overviewDescription')}
                  </p>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                    <div className="bg-blue-900/30 rounded-lg p-6 border border-blue-800">
                      <h3 className="text-xl font-bold mb-3 flex items-center">
                        <Zap className="w-5 h-5 mr-2 text-blue-400" />
                        {t('pages.apiDocs.features')}
                      </h3>
                      <ul className="space-y-2 text-gray-300">
                        <li>{t('pages.apiDocs.featuresList.item1')}</li>
                        <li>{t('pages.apiDocs.featuresList.item2')}</li>
                        <li>{t('pages.apiDocs.featuresList.item3')}</li>
                        <li>{t('pages.apiDocs.featuresList.item4')}</li>
                        <li>{t('pages.apiDocs.featuresList.item5')}</li>
                      </ul>
                    </div>
                    
                    <div className="bg-green-900/30 rounded-lg p-6 border border-green-800">
                      <h3 className="text-xl font-bold mb-3 flex items-center">
                        <Shield className="w-5 h-5 mr-2 text-green-400" />
                        {t('pages.apiDocs.security')}
                      </h3>
                      <ul className="space-y-2 text-gray-300">
                        <li>{t('pages.apiDocs.securityList.item1')}</li>
                        <li>{t('pages.apiDocs.securityList.item2')}</li>
                        <li>{t('pages.apiDocs.securityList.item3')}</li>
                        <li>{t('pages.apiDocs.securityList.item4')}</li>
                        <li>{t('pages.apiDocs.securityList.item5')}</li>
                      </ul>
                    </div>
                  </div>

                  <div className="bg-gray-800 rounded-lg p-6 mb-6">
                    <h3 className="text-xl font-bold mb-4">{t('pages.apiDocs.baseUrl')}</h3>
                    <code className="text-green-400 text-lg font-mono bg-black px-4 py-2 rounded block">
                      https://api.render.audome.io
                    </code>
                    
                    <div className="mt-4 bg-purple-900/30 rounded-lg p-4 border border-purple-800">
                      <h4 className="font-bold text-purple-400 mb-2">{t('pages.apiDocs.privacyTitle')}</h4>
                      <p className="text-gray-300 text-sm mb-2">
                        <strong>{t('pages.apiDocs.privacyDescription')}</strong>
                      </p>
                      <ul className="list-disc list-inside space-y-1 text-sm text-gray-300 mr-4">
                        <li>{t('pages.apiDocs.privacyList.item1')}</li>
                        <li>{t('pages.apiDocs.privacyList.item2')}</li>
                        <li>{t('pages.apiDocs.privacyList.item3')}</li>
                        <li>{t('pages.apiDocs.privacyList.item4')}</li>
                        <li>{t('pages.apiDocs.privacyList.item5')}</li>
                      </ul>
                      <div className="mt-3 bg-green-900/20 rounded p-2 border border-green-800">
                        <p className="text-xs text-green-300">
                          {t('pages.apiDocs.privacyTrust')}
                        </p>
                      </div>
                    </div>

                    <div className="mt-4 bg-blue-900/30 rounded-lg p-4 border border-blue-800">
                      <h4 className="font-bold text-blue-400 mb-2">{t('pages.apiDocs.n8nIntegration')}</h4>
                      <p className="text-gray-300 text-sm mb-2">
                        {t('pages.apiDocs.n8nDescription')}
                      </p>
                      <div className="bg-black rounded p-3 mt-2" dir="ltr" style={{ direction: 'ltr', textAlign: 'left' }}>
                        <code className="text-green-400 font-mono text-sm">npm install n8n-nodes-audome</code>
                      </div>
                      <p className="text-gray-300 text-xs mt-2">
                        {t('pages.apiDocs.n8nAfterInstall')}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'authentication' && (
              <div className="space-y-8">
                <div className="bg-gray-900 rounded-lg p-8">
                  <h2 className="text-3xl font-bold mb-4 flex items-center">
                    <Shield className="w-8 h-8 mr-3 text-blue-400" />
                    {t('pages.apiDocs.authTitle')}
                  </h2>
                  <p className="text-gray-300 text-lg mb-6">
                    {t('pages.apiDocs.authDescription')}
                  </p>
                  
                  <div className="bg-gray-800 rounded-lg p-6 mb-6">
                    <h3 className="text-xl font-bold mb-4">{t('pages.apiDocs.requiredHeader')}</h3>
                    <code className="text-green-400 font-mono text-sm block">
                      Authorization: Bearer YOUR_API_TOKEN
                    </code>
                    
                    <div className="mt-4 bg-green-900/30 rounded-lg p-4 border border-green-800">
                      <h4 className="font-bold text-green-400 mb-2">{t('pages.apiDocs.tokenValidity')}</h4>
                      <p className="text-gray-300 text-sm">
                        {t('pages.apiDocs.tokenValidityDescription')}
                      </p>
                    </div>
                  </div>

                  <div className="bg-red-900/30 rounded-lg p-6 border border-red-800">
                    <h3 className="text-xl font-bold mb-3 flex items-center">
                      <AlertCircle className="w-5 h-5 mr-2 text-red-400" />
                      {t('pages.apiDocs.errorExample')}
                    </h3>
                    <div className="bg-black rounded p-4" dir="ltr" style={{ direction: 'ltr', textAlign: 'left' }}>
                      <code className="text-red-400 font-mono text-sm">
                        HTTP/1.1 401 Unauthorized<br/>
                        {"{"}<br/>
                        &nbsp;&nbsp;"error": "API token required"<br/>
                        {"}"}
                      </code>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'endpoints' && (
              <div className="space-y-8">
                <div className="bg-gray-900 rounded-lg p-8">
                  <h2 className="text-3xl font-bold mb-4 flex items-center">
                    <Terminal className="w-8 h-8 mr-3 text-blue-400" />
                    {t('pages.apiDocs.endpointsTitle')}
                  </h2>
                  
                  <div className="space-y-6">
                    <div className="bg-gray-800 rounded-lg p-6">
                      <div className="flex items-center mb-4">
                        <span className="bg-blue-600 text-white px-3 py-1 rounded text-sm font-bold mr-3">
                          {t('pages.apiDocs.getProjects')}
                        </span>
                        <code className="text-green-400 font-mono text-lg">
                          /api/projects
                        </code>
                      </div>
                      
                      <p className="text-gray-300 mb-4">
                        {t('pages.apiDocs.getProjectsDescription')}
                      </p>
                      
                      <div className="mb-4">
                        <h4 className="font-bold mb-2">{t('pages.apiDocs.response')}</h4>
                        <div className="bg-black rounded p-4 overflow-x-auto" dir="ltr" style={{ direction: 'ltr', textAlign: 'left' }}>
                          <code className="text-green-400 font-mono text-sm whitespace-pre">
                            {"{"}<br/>
                            &nbsp;&nbsp;"success": true,<br/>
                            &nbsp;&nbsp;"projects": [<br/>
                            &nbsp;&nbsp;&nbsp;&nbsp;{"{"}<br/>
                            &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;"id": "project-id",<br/>
                            &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;"name": "Project Name",<br/>
                            &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;"layers": [<br/>
                            &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;{"{"}"name": "title", "type": "text", "required": true{"}"}<br/>
                            &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;],<br/>
                            &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;"curl_example": "..."<br/>
                            &nbsp;&nbsp;&nbsp;&nbsp;{"}"}<br/>
                            &nbsp;&nbsp;]<br/>
                            {"}"}
                          </code>
                        </div>
                      </div>
                    </div>

                    <div className="bg-gray-800 rounded-lg p-6">
                      <div className="flex items-center mb-4">
                        <span className="bg-green-600 text-white px-3 py-1 rounded text-sm font-bold mr-3">
                          {t('pages.apiDocs.postGenerate')}
                        </span>
                        <code className="text-green-400 font-mono text-lg">
                          /api/generate/{`<project_id>`}
                        </code>
                      </div>
                      
                      <p className="text-gray-300 mb-4">
                        {t('pages.apiDocs.postGenerateDescription')}
                      </p>
                      
                      <div className="mb-4">
                        <h4 className="font-bold mb-2">{t('pages.apiDocs.requiredParams')}</h4>
                        <div className="bg-black rounded p-4" dir="ltr" style={{ direction: 'ltr', textAlign: 'left' }}>
                          <code className="text-blue-400 font-mono text-sm">
                            {"{"}<br/>
                            &nbsp;&nbsp;"data": {"{"}<br/>
                            &nbsp;&nbsp;&nbsp;&nbsp;"layer_name": "value", // Names from Projects page<br/>
                            &nbsp;&nbsp;&nbsp;&nbsp;"another_layer": "value"<br/>
                            &nbsp;&nbsp;{"}"}<br/>
                            {"}"}
                          </code>
                        </div>
                        <p className="text-gray-400 text-sm mt-2">
                          {t('pages.apiDocs.useGetProjects')}
                        </p>
                      </div>
                      
                      <div className="mb-4">
                        <h4 className="font-bold mb-2">{t('pages.apiDocs.response')}</h4>
                        <div className="bg-black rounded p-4" dir="ltr" style={{ direction: 'ltr', textAlign: 'left' }}>
                          <code className="text-green-400 font-mono text-sm">
                            {"{"}<br/>
                            &nbsp;&nbsp;"success": true,<br/>
                            &nbsp;&nbsp;"image_url": "https://api.render.audome.io/images/20251028_084748_04dc1404.png",<br/>
                            &nbsp;&nbsp;"generation_id": "26af4161-1e0f-47f4-89fd-ef492c64cd41",<br/>
                            &nbsp;&nbsp;"credits_remaining": 8,<br/>
                            &nbsp;&nbsp;"metadata": {"{"}<br/>
                            &nbsp;&nbsp;&nbsp;&nbsp;"generationTime": 3591,<br/>
                            &nbsp;&nbsp;&nbsp;&nbsp;"height": 1350,<br/>
                            &nbsp;&nbsp;&nbsp;&nbsp;"width": 1080<br/>
                            &nbsp;&nbsp;{"}"}<br/>
                            {"}"}
                          </code>
                        </div>
                        <div className="mt-3 bg-orange-900/30 rounded-lg p-3 border border-orange-800">
                          <p className="text-xs text-gray-300">
                            {t('pages.apiDocs.imageDeleted')}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'examples' && (
              <div className="space-y-8">
                <div className="bg-gray-900 rounded-lg p-8">
                  <h2 className="text-3xl font-bold mb-4 flex items-center">
                    <Code className="w-8 h-8 mr-3 text-blue-400" />
                    {t('pages.apiDocs.codeExamplesTitle')}
                  </h2>
                  
                  <div className="space-y-6">
                    {Object.entries(codeExamples).map(([language, code]) => (
                      <div key={language} className="bg-gray-800 rounded-lg p-6">
                        <div className="flex items-center justify-between mb-4">
                          <h3 className="text-xl font-bold capitalize">
                            {language === 'curl' ? 'cURL' : language}
                          </h3>
                          <button
                            onClick={() => copyToClipboard(code, language)}
                            className="bg-blue-600 hover:bg-blue-700 px-4 py-2 rounded flex items-center transition-colors"
                          >
                            {copiedToken === language ? (
                              <CheckCircle className="w-4 h-4 mr-2" />
                            ) : (
                              <Copy className="w-4 h-4 mr-2" />
                            )}
                            {copiedToken === language ? t('pages.apiDocs.copied') : t('pages.apiDocs.copy')}
                          </button>
                        </div>
                        <pre className="bg-black rounded p-4 overflow-x-auto" dir="ltr" style={{ direction: 'ltr', textAlign: 'left' }}>
                          <code className="text-gray-300 font-mono text-sm">{code}</code>
                        </pre>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'tokens' && (
              <div className="space-y-8">
                <div className="bg-gray-900 rounded-lg p-8">
                  <h2 className="text-3xl font-bold mb-4 flex items-center">
                    <Key className="w-8 h-8 mr-3 text-blue-400" />
                    {t('pages.apiDocs.tokenManagementTitle')}
                  </h2>
                  
                  <div className="bg-blue-900/30 rounded-lg p-6 mb-8 border border-blue-800">
                    <h3 className="text-xl font-bold mb-4 text-blue-300">{t('pages.apiDocs.tokenManagementDescription')}</h3>
                    <p className="text-gray-300 mb-4">
                      {t('pages.apiDocs.tokenManagementSubtitle')}
                    </p>
                    <button
                      onClick={() => window.location.href = '/api-tokens'}
                      className="bg-blue-600 hover:bg-blue-700 px-6 py-3 rounded-lg font-bold transition-colors flex items-center gap-2"
                    >
                      <Key className="w-5 h-5" />
                      {t('pages.apiDocs.goToTokenManagement')}
                    </button>
                  </div>

                  <div className="bg-gray-800 rounded-lg p-6">
                    <h3 className="text-xl font-bold mb-4">{t('pages.apiDocs.tokenInfo')}</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="bg-black rounded-lg p-4">
                        <h4 className="font-bold mb-2 text-green-400">{t('pages.apiDocs.activeTokens')}</h4>
                        <p className="text-gray-300 text-sm">
                          {t('pages.apiDocs.activeTokensDescription')}
                        </p>
                      </div>
                      <div className="bg-black rounded-lg p-4">
                        <h4 className="font-bold mb-2 text-green-400">{t('pages.apiDocs.neverExpire')}</h4>
                        <p className="text-gray-300 text-sm">
                          {t('pages.apiDocs.neverExpireDescription')}
                        </p>
                      </div>
                      <div className="bg-black rounded-lg p-4">
                        <h4 className="font-bold mb-2 text-blue-400">{t('pages.apiDocs.securityInfo')}</h4>
                        <p className="text-gray-300 text-sm">
                          {t('pages.apiDocs.securityInfoDescription')}
                        </p>
                      </div>
                      <div className="bg-black rounded-lg p-4">
                        <h4 className="font-bold mb-2 text-purple-400">{t('pages.apiDocs.usageTracking')}</h4>
                        <p className="text-gray-300 text-sm">
                          {t('pages.apiDocs.usageTrackingDescription')}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ApiDocumentation;
